import { useCallback, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui';
import {
  isLyrraPublicEduflowPlayerUrl,
  openLyrraEduflowEmbedModal,
  toAbsoluteLyrraPlayerUrlForEmbed,
  withEmbedPlayerSearchParams,
} from '@/lib/lyrraEduflowEmbedSnippet';
import {
  findHelpTopicById,
  type HelpCategory,
  HELP_TOPIC_ONBOARDING_AI_DESIGNER_ID,
  HELP_TOPIC_ONBOARDING_BLOCK_TYPES_ID,
  HELP_TOPIC_ONBOARDING_BRANCHES_ID,
  HELP_TOPIC_ONBOARDING_CATALOG_VISIBILITY_ID,
  HELP_TOPIC_ONBOARDING_CERTIFICATES_ID,
  HELP_TOPIC_ONBOARDING_DASHBOARD_ID,
  HELP_TOPIC_ONBOARDING_DESIGNER_OVERVIEW_ID,
  HELP_TOPIC_ONBOARDING_ENROLLMENTS_ID,
  HELP_TOPIC_ONBOARDING_GROUPS_COHORTS_ID,
  HELP_TOPIC_ONBOARDING_MEDIA_LIBRARY_ID,
  HELP_TOPIC_ONBOARDING_NAVIGATION_ID,
  HELP_TOPIC_ONBOARDING_OBJECTIVES_ID,
  HELP_TOPIC_ONBOARDING_PROFILE_ID,
  HELP_TOPIC_ONBOARDING_PROGRESS_ID,
  HELP_TOPIC_ONBOARDING_PUBLICATION_LINKS_ID,
  HELP_TOPIC_ONBOARDING_PUBLICATION_STATUS_ID,
  HELP_TOPIC_ONBOARDING_ROLES_ID,
  HELP_TOPIC_ONBOARDING_VALIDATION_ID,
} from './help-center-topics';
import { HelpTopicModal } from './HelpTopicModal';
import { currentUiLangCode } from '@/lib/ui-languages';
import {
  buildOnboardingHelpPublicPlayerUrl,
  fetchOnboardingHelpFlowId,
  type OnboardingHelpKind,
} from './onboarding-dashboard-player';

function openManagedFormationUrl(url: string, navigate: NavigateFunction) {
  const raw = url.trim();
  if (!raw) return;
  if (raw.startsWith('/')) {
    if (raw.startsWith('//')) return;
    navigate(raw);
    return;
  }
  try {
    const u = new URL(raw);
    if (typeof window !== 'undefined' && u.origin === window.location.origin) {
      navigate(`${u.pathname}${u.search}${u.hash}`);
      return;
    }
  } catch {
    /* ignore */
  }
  window.open(raw, '_blank', 'noopener,noreferrer');
}

function openManagedFormationAsEmbedIfPlayer(
  formationUrl: string,
  modalTitle: string,
  uiLang: string | undefined,
  tClose: string,
  navigate: NavigateFunction,
) {
  const raw = formationUrl.trim();
  if (!raw) return;
  if (!isLyrraPublicEduflowPlayerUrl(raw)) {
    openManagedFormationUrl(raw, navigate);
    return;
  }
  const absolute = toAbsoluteLyrraPlayerUrlForEmbed(raw);
  const embedSrc = withEmbedPlayerSearchParams(absolute, uiLang ? { lang: uiLang } : undefined);
  openLyrraEduflowEmbedModal(embedSrc, {
    modalAriaLabel: modalTitle,
    closeAriaLabel: tClose,
  });
}

export type HelpTopicLauncherOptions = {
  /** topicId (préfixe managed-topic:) → URL formation */
  managedFormationUrls?: Record<string, string>;
  /** Arborescence courante (API) pour modale et résolution des liens « Voir aussi ». */
  helpCategories?: readonly HelpCategory[] | null;
};

/**
 * Ouvre la même modale (ou player onboarding) que sur /aide — partagé entre HelpPage et le chat support.
 */
export function useHelpTopicLauncher(opts?: HelpTopicLauncherOptions) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [openingOnboardingPlayer, setOpeningOnboardingPlayer] = useState(false);
  const onboardingOpenInFlight = useRef(false);

  const selectedContext = selectedTopicId
    ? findHelpTopicById(selectedTopicId, opts?.helpCategories ?? null)
    : null;

  const openOnboardingHelpPlayer = useCallback(
    async (kind: OnboardingHelpKind) => {
      if (onboardingOpenInFlight.current) return;
      onboardingOpenInFlight.current = true;
      setOpeningOnboardingPlayer(true);
      try {
        const flowId = await fetchOnboardingHelpFlowId(kind);
        if (!flowId) {
          toast.error(t('help.onboardingFlowMissing'), {
            description: t('help.onboardingFlowMissingDesc'),
          });
          return;
        }
        const uiLang = currentUiLangCode(i18n.resolvedLanguage ?? i18n.language);
        const url = buildOnboardingHelpPublicPlayerUrl(flowId, uiLang);
        const titleKey = `help.onboarding.${kind}`;
        openLyrraEduflowEmbedModal(url, {
          modalAriaLabel: t(titleKey),
          closeAriaLabel: t('btn.close'),
        });
      } finally {
        onboardingOpenInFlight.current = false;
        setOpeningOnboardingPlayer(false);
      }
    },
    [t, i18n.resolvedLanguage, i18n.language],
  );

  const openTopic = useCallback(
    (topicId: string) => {
      const managedUrl = opts?.managedFormationUrls?.[topicId];
      if (managedUrl) {
        setSelectedTopicId(null);
        const resolved = findHelpTopicById(topicId, opts?.helpCategories ?? null);
        const modalTitle = resolved?.topic.title ?? t('help.title');
        const uiLang = currentUiLangCode(i18n.resolvedLanguage ?? i18n.language);
        openManagedFormationAsEmbedIfPlayer(managedUrl, modalTitle, uiLang, t('btn.close'), navigate);
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_DASHBOARD_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('dashboard');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_NAVIGATION_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('navigation');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_PROFILE_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('profile');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_ROLES_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('roles');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_BLOCK_TYPES_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('blocks');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_DESIGNER_OVERVIEW_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('designer-overview');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_BRANCHES_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('branches');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_VALIDATION_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('validation');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_OBJECTIVES_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('objectives');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_AI_DESIGNER_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('ai-designer');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_PUBLICATION_STATUS_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('publication-status');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_PUBLICATION_LINKS_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('publication-links');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_CATALOG_VISIBILITY_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('catalog-visibility');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_ENROLLMENTS_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('enrollments');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_GROUPS_COHORTS_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('groups-cohorts');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_PROGRESS_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('progress');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_CERTIFICATES_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('certificates');
        return;
      }
      if (topicId === HELP_TOPIC_ONBOARDING_MEDIA_LIBRARY_ID) {
        setSelectedTopicId(null);
        void openOnboardingHelpPlayer('media-library');
        return;
      }
      setSelectedTopicId(topicId);
    },
    [openOnboardingHelpPlayer, navigate, opts?.managedFormationUrls, opts?.helpCategories, t, i18n.resolvedLanguage, i18n.language],
  );

  const helpTopicModal = (
    <HelpTopicModal
      open={Boolean(selectedContext)}
      onClose={() => setSelectedTopicId(null)}
      category={selectedContext?.category ?? null}
      topic={selectedContext?.topic ?? null}
      onOpenTopic={openTopic}
      lookupCategories={opts?.helpCategories ?? null}
    />
  );

  return {
    openTopic,
    openingOnboardingPlayer,
    helpTopicModal,
  };
}
